/**
 * Cache Busting Utilities
 *
 * This module provides utilities to detect when a new version of the app
 * is deployed and force a refresh to ensure users get the latest code.
 */

const APP_VERSION_KEY = 'app_version';
const BUILD_TIME_KEY = 'build_time';

/**
 * Check if we're in a browser environment
 */
const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

/**
 * Get app version from meta tag
 */
export const getAppVersionFromMeta = () => {
  if (!isBrowser()) return null;
  const metaTag = document.querySelector('meta[name="app-version"]');
  return metaTag ? metaTag.getAttribute('content') : null;
};

/**
 * Get build timestamp from meta tag
 */
export const getBuildTimeFromMeta = () => {
  if (!isBrowser()) return null;
  const metaTag = document.querySelector('meta[name="build-time"]');
  return metaTag ? metaTag.getAttribute('content') : null;
};

/**
 * Store current version in localStorage
 */
export const storeCurrentVersion = () => {
  if (!isBrowser() || typeof localStorage === 'undefined') return;

  const version = getAppVersionFromMeta();
  const buildTime = getBuildTimeFromMeta();

  if (version) {
    localStorage.setItem(APP_VERSION_KEY, version);
  }
  if (buildTime) {
    localStorage.setItem(BUILD_TIME_KEY, buildTime);
  }
};

/**
 * Check if a new version is available
 */
export const isNewVersionAvailable = () => {
  if (!isBrowser() || typeof localStorage === 'undefined') return false;

  const currentVersion = getAppVersionFromMeta();
  const storedVersion = localStorage.getItem(APP_VERSION_KEY);

  // If no stored version, this is first load
  if (!storedVersion) {
    return false;
  }

  // Compare versions
  return currentVersion && currentVersion !== storedVersion;
};


/**
 * Force reload the page and clear cache
 */
export const forceReloadAndClearCache = async () => {
  try {
    // Clear all caches if Cache API is supported
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('Browser caches cleared');
    }

    // Unregister service workers if available
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));
      console.log('Service workers unregistered');
    }

    // Store the new version before reload
    storeCurrentVersion();

    // Force hard reload with cache bypass
    // Try the modern way first, fallback to legacy
    if (window.location.reload) {
      window.location.reload();
    }
  } catch (error) {
    console.warn('Error during cache clearing:', error);
    // Even if cache clearing fails, reload anyway
    window.location.reload();
  }
};

/**
 * Initialize version checking
 * Call this when the app loads
 *
 * This only checks on initial page load - no periodic background checks
 */
export const initVersionCheck = () => {
  // Only run in browser environment
  if (!isBrowser()) {
    return;
  }

  try {
    // Check if new version on load
    if (isNewVersionAvailable()) {
      console.log('New version detected, clearing cache and reloading...');
      forceReloadAndClearCache();
      return;
    }

    // Store current version for next visit
    storeCurrentVersion();
  } catch (error) {
    // If cache busting fails, log but don't break the app
    console.warn('Cache busting initialization failed:', error);
  }
};

/**
 * Clear all app cache manually
 */
export const clearAppCache = async () => {
  if (!isBrowser()) return;

  try {
    // Clear localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(APP_VERSION_KEY);
      localStorage.removeItem(BUILD_TIME_KEY);
    }

    // Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    console.log('App cache cleared successfully');
  } catch (error) {
    console.warn('Failed to clear app cache:', error);
  }
};

/**
 * Get cache busting query parameter
 * Useful for appending to asset URLs to force reload
 */
export const getCacheBustingParam = () => {
  const version = getAppVersionFromMeta();
  const buildTime = getBuildTimeFromMeta();

  if (version && buildTime) {
    return `v=${version}&t=${buildTime}`;
  } else if (version) {
    return `v=${version}`;
  } else if (buildTime) {
    return `t=${buildTime}`;
  }

  return `t=${Date.now()}`;
};

/**
 * Add cache busting parameter to a URL
 * Example: addCacheBusting('/api/data') => '/api/data?v=8.3.1&t=1234567890'
 */
export const addCacheBusting = (url) => {
  if (!url) return url;

  const param = getCacheBustingParam();
  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}${param}`;
};
